// Generic import
import _ from 'lodash';
import axios from 'axios';
import validator from 'email-validator';

// Local imports
import { createEmptyProduct, findMatchingProducts, checkProductErrors } from './product.js';
import PRODUCT_TYPES from '../../ProductTypes.js';

function minimizeProducts({ products, transportationMethod }) {
    // Filter out LCL products with empty calculations array (empty LCL placeholder from opportunity editor)
    // LCL products without a calculations property at all (rate products) are kept
    let sentProducts = products.filter(p => {
        if (p.type === 'LCL' && p.hasOwnProperty('calculations') && p.calculations.length === 0) return false;
        if (p.type === 'Air freight' && p.hasOwnProperty('calculations') && p.calculations.length === 0) return false;
        return true;
    }).map(p => {
        // A rate/product whose type isn't a known PRODUCT_TYPES entry for this mode (e.g. a
        // malformed cost with an invalid/blank product type) would make .find() return
        // undefined and crash the destructure. Fall back to no type-specific fields so the
        // cost can still be dragged in instead of blowing up the whole quote.
        const match = (transportationMethod ?
            (PRODUCT_TYPES[transportationMethod] || []).find(p2 => p2.name === p.type) :
            _.flatten(_.keys(PRODUCT_TYPES).map(tm => PRODUCT_TYPES[tm])).find(p2 => p2.name === p.type)) || {};
        const { ask = [], required = [], type } = match;
        const fields = _.flatten(ask.map(a => {
            if (a === 'dangerous') {
                return ['dangerous', 'dangerousMSDS', 'dangerousClass', 'dangerousUNCode'];
            } else if ((a === 'weight') && ((required.includes(a)) || (p.weight))) {
                return ['kgOrLbs', 'weight'];
            } else if (a === 'chargeable-weight') {
                if (p.height && p.width && p.length) {
                    return ['kgOrLbs', 'height', 'width', 'length', 'cmOrInches', 'weight'];
                } else if (p.volume) {
                    return ['kgOrLbs', 'volume', 'cmOrInches', 'weight'];
                } else {
                    return ['kgOrLbs', 'chargeableWeight', 'weight'];
                }
            } else if (a === 'weight-or-measurement') {
                return ['weight', 'kgOrLbs', 'volume'];
            } else if (a === 'non-stackable') {
                return ['nonstackable'];
            } else if (a === 'dimensions') {
                return ['width', 'height', 'length', 'cmOrInches'];
            } else if (a === 'quantity') {
                return ['quantity'];
            } else if (a === 'commodity') {
                return ['commodity'];
            } else if ((a === 'temperature') && ((required.includes(a)) || (p.temperature))) {
                return ['temperature', 'celsiusOrFahrenheit'];
            } else if ((a === 'height') && ((required.includes(a)) || (p.height))) {
                return ['height', 'cmOrInches'];
            } else if (a === 'lcl-calculations') {
                return ['calculations', 'kgOrLbs', 'cmOrInches', 'densityRatio'];
            } else if (a === 'air-calculations') {
                return ['calculations', 'kgOrLbs', 'cmOrInches', 'roundChargeableWeight'];
            };
            return [];
        }));
        const clean = _.pickBy(_.pick(p, ['type', 'incoterm', 'id', ...fields]), v => !_.isUndefined(v));
        const parsed = _.mapValues(clean, (v, k) => {
            if (['width', 'height', 'length', 'temperature', 'weight', 'volume', 'chargeableWeight', 'weightOrMeasurment'].includes(k)) return parseFloat(v);
            if (['quantity'].includes(k)) return parseInt(v);
            if (k === 'calculations' && Array.isArray(v)) {
                return v.map(calc => ({
                    ...calc,
                    ...(calc.length != null ? { length: parseFloat(calc.length) } : {}),
                    ...(calc.width != null ? { width: parseFloat(calc.width) } : {}),
                    ...(calc.height != null ? { height: parseFloat(calc.height) } : {}),
                    ...(calc.weight != null ? { weight: parseFloat(calc.weight) } : {}),
                    ...(calc.volume != null ? { volume: parseFloat(calc.volume) } : {}),
                    ...(calc.chargeableWeight != null ? { chargeableWeight: parseFloat(calc.chargeableWeight) } : {}),
                    ...(calc.quantity != null ? { quantity: parseInt(calc.quantity) } : {}),
                }));
            }
            return v;
        });
        return { ...parsed, isCombined: type === 'combined' };
    });

    const combinedTypes = _.uniq(sentProducts.filter(p => p.isCombined).map(p => p.type));

    const newProductsForCombined = combinedTypes.map(t => {
        const products = sentProducts.filter(p => p.type === t);
        const fields = _(products).map(p => _.keys(p)).flatten().uniq().without('type').value();
        return {
            type: t,
            ...fields.reduce((a, v) => ({ ...a, [v]: products.map(p => p[v])}), {}) 
        }
    });

    sentProducts = [
        ...sentProducts.filter(p => !combinedTypes.includes(p.type)),
        ...newProductsForCombined,
    ];

    return sentProducts.map(p => _.omit(p, ['isCombined']));
}

function maximizeProducts({ products }) {
    const _products = (products || []).map(p => ({ ...createEmptyProduct(), ...p }));
    return _products.map(p => {
        if (p.type === 'Air Freight' || p.type === 'Air freight') {
            if (p.volume) {
                delete p.chargeableWeight;
                delete p.height;
                delete p.width;
                delete p.length;
            } else if (p.height) {
                delete p.chargeableWeight;
                delete p.volume;
            } else if (p.chargeableWeight) {
                delete p.height;
                delete p.width;
                delete p.length;
                delete p.volume;
            }
        }
        return p;
    });
}

function getProductInOffer({ offer, products }) {
    const product = _.first(_.compact(offer.sections.map(section => {
        const field = section.offers[0].fields.find(f => f.type === 'per-unit' || f.type === 'per-unit-type' || f.type === 'per-unit-weight' || f.type === 'per-unit-type-weight');
        if (field) {
            const fieldValue = _.keys(field.values).find(key => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(key));
            return products.find(p => p.id === fieldValue);
        }

        const k = _.compact(section.offers[0].fields.map(f => _.first(_.without(_.keys(f.values), ['string', 'flat', 'custom', 'date']))));
        return products.find(p => p.id === k[0]);
    })));
    if (product) return product;
    // FCL (and any per-shipment) rates carry all charges as `flat` fields that don't reference the
    // product id, so the scan above finds nothing. When the offer knows its own product (rates
    // carry it via convertRateToOffer), resolve from that instead of letting callers fall back to
    // an arbitrary products[index]. Opportunity offers have no `.product`, so they're unaffected.
    if (offer.product) return products.find(p => p.id === offer.product.id) || offer.product;
    return product;
}

function migrateOpportunity({ opportunity }) {
    opportunity.offers?.forEach(o => 
        o.sections?.forEach(s => 
            s.offers?.forEach(o => 
                o.fields?.forEach(f =>
                    f.buyingRates = _.flatten(f.buyingRates?.map(r => {
                        if (r.opportunityProductId) return r;
                        const rate = opportunity.buyingRates?.find(r2 => r2.id === r.rateId);
                        // Ref points at a rate that no longer exists — drop it (the second pass
                        // below would drop it anyway). Guards against a crash on rate.product.
                        if (!rate?.product) return [];
                        const possibleProducts = findMatchingProducts({
                            products: opportunity.products,
                            product: rate.product
                        });
                        return (f.type === 'flat' && !_.isEmpty(possibleProducts) ? [possibleProducts[0]] : possibleProducts).map(p => ({
                            ...r,
                            opportunityProductId: p.id
                        }));
                    }))
                )
            )
        )
    );

    opportunity.offers?.forEach(o => 
        o.sections?.forEach(s => 
            s.offers?.forEach(o => 
                o.fields?.forEach(f =>
                    f.buyingRates = _.compact(f.buyingRates?.map(br => {
                        const rate = opportunity.buyingRates.find(r => r.id === br.rateId);
                        if (!rate) {
                            console.log('[migrateOpportunity] buying rate not found', br);
                            return null;
                        }
                        return br;
                    }))
                )
            )
        )
    );
    return opportunity;
}

function setProductForOffer({ offer, product, organization }) {
    const o = _.cloneDeep(offer);
    o.sections.forEach(section => section.offers[0].fields.forEach(field => {
        if (field.type === 'per-unit' || field.type === 'per-unit-type') {
            const currency = _.values(field.values)[0]?.currency;
            const prevValue = _.values(field.values)[0]?.value || 0;
            const prevFormula = _.values(field.values)[0]?.formula || null;
            field.values = { [product.id]: { value: prevValue, formula: prevFormula, currency: currency || organization.defaults?.currency }, string: { value: '' }, date: { value: '' } };
        }
    }));
    return o;
}

function openOpportunitiesDb({ organizationId, me }) {
    return new Promise((res, rej) => {
        const request = indexedDB.open(`${organizationId}-${me.email}-opportunities`, 1);
        request.onerror = (event) => {
            console.error('Why didn\'t you allow my web app to use IndexedDB?!', event);
            rej(event.target.error);
        };
        request.onsuccess = (event) => {
            console.log('[Opportunities: getOpportunitiesFromServer] Database opened successfully.');
            res(event.target.result);
        };
        request.onupgradeneeded = (event) => {
            console.log('[Opportunities: getOpportunitiesFromServer] Database onupgradeneeded called.');
            const db = event.target.result;
            db.createObjectStore('opportunities', { keyPath: 'id' });
        };
    });
}

async function putOpportunities({ db, items }) {
    const transaction = db.transaction(['opportunities'], 'readwrite');
    const store = transaction.objectStore('opportunities');
    console.log('[Opportunities: getOpportunitiesFromServer] Adding', items.length, 'opportunities to IndexedDB.');
    for (let i = 0; i < items.length; i++) {
        const r = items[i];
        try {
            await new Promise((res, rej) => {
                const request = store.put(r); // TODO: Check if exists and update
                request.onsuccess = () => res();
                request.onerror = (event) => rej(event.target.error);
            });
        } catch (e) { }
    }
    console.log('[Opportunities: getOpportunitiesFromServer] Done adding', items.length, 'opportunities to IndexedDB.');
}

async function getOpportunitiesFromServer({ accesstoken, organizationId, me, onProgress, since = null }) {

    let db = null;
    let cache = null;

    if (!since) {
        db = await openOpportunitiesDb({ organizationId, me });

        // Get existing opportunities from IndexedDB
        cache = await new Promise((res, rej) => {
            console.log('[Opportunities: getOpportunitiesFromServer] Reading from IndexedDB.');
            const transaction = db.transaction(['opportunities'], 'readonly');
            const store = transaction.objectStore('opportunities');
            const request = store.getAll();
            request.onsuccess = (event) => {
                console.log('[Opportunities: getOpportunitiesFromServer] Done reading', event.target.result.length, 'from IndexedDB.');
                res(event.target.result);
            };
            request.onerror = (event) => {
                rej(event.target.error);
            }
        });
    }

    const latest = since ? since : (_.max(cache.map(r => parseInt(r.modified))) || 0) + 1;

    const items = [];
    let cursor = null;
    let shouldResetLocalDB = false;
    console.log(`[Opportunities: getOpportunitiesFromServer] Starting /api/opportunities?organizationId=${organizationId}&since=${latest}`);
    do {
        const { data } = await axios(`/api/opportunities?organizationId=${organizationId}&accesstoken=${accesstoken}&since=${latest}&cursor=${cursor ? cursor : ''}`);
        if (_.isArray(data)) return data;
        cursor = data.cursor;
        shouldResetLocalDB = data.shouldResetLocalDB;
        items.push(...data.opportunities);
        if (onProgress) onProgress({ total: data.count, count: items.length });
        console.log(`[Opportunities: getOpportunitiesFromServer] Read ${items.length} items. Has more? ${cursor ? 'Yes' : 'No'}`);
    } while (cursor);

    if (since) {
        if (shouldResetLocalDB) {
            throw new Error('Reset requested');
        }
        const updated = items.filter(r => r.id !== `${organizationId}-reset`);
        // A poll used to hand its updates to the caller and leave the cache holding the
        // older copy. The next load reads that cache and asks the server only for what
        // changed after the newest thing in it, so the copy it already has stale is never
        // fetched again - which is why a figure would put itself right while the page stayed
        // open and come back wrong on the next refresh. Write down what we just fetched too.
        if (updated.length > 0) {
            try {
                const pollDb = await openOpportunitiesDb({ organizationId, me });
                await putOpportunities({ db: pollDb, items: updated });
            } catch (e) {
                // The cache is an optimisation; the caller still gets the fresh data.
                console.error('[Opportunities: getOpportunitiesFromServer] Could not cache polled updates:', e);
            }
        }
        return updated;
    }

    // Clear IndexexDB if requested
    if (shouldResetLocalDB) {
        console.log('[Opportunities: getOpportunitiesFromServer] Reset requested.');

        // Clear IndexedDB
        const transaction = db.transaction(['opportunities'], 'readwrite');
        const store = transaction.objectStore('opportunities');
        try {
            await new Promise((res, rej) => {
                const request = store.clear();
                request.onsuccess = (event) => {
                    console.log('[Opportunities: getOpportunitiesFromServer] IndexedDB cleared.');
                    res();
                };
                request.onerror = (event) => {
                    console.error('[Opportunities: getOpportunitiesFromServer] Error clearing IndexedDB.', event.target.error);
                    rej(event.target.error);
                }
            });
        } catch (e) { }
    }

    await putOpportunities({ db, items });
    if (shouldResetLocalDB) {
        return items.filter(r => r.id !== `${organizationId}-reset`);
    } else {
        return _.uniqBy([...items, ...cache], 'id').filter(r => r.id !== `${organizationId}-reset`);
    }
}

function getDisplayRate({ sourceCurrency, displayCurrency, displayCurrencySource, lockedExchangeRates, liveRates, fixedRates }) {
    if (displayCurrencySource === 'fixed' && !_.isEmpty(lockedExchangeRates)) {
        const lockedFixed = (lockedExchangeRates.organization || []).find(r => r.from === sourceCurrency && r.to === displayCurrency);
        if (lockedFixed) return lockedFixed.rate;
        if (lockedExchangeRates.live?.[sourceCurrency]) return lockedExchangeRates.live[displayCurrency] / lockedExchangeRates.live[sourceCurrency];
    }

    if (displayCurrencySource === 'fixed' && _.isEmpty(lockedExchangeRates)) {
        if (!fixedRates) {
            console.warn('[getDisplayRate] Fixed rates are empty');
        } else {
            const fixedRate = fixedRates.find(r => r.from === sourceCurrency && r.to === displayCurrency);
            if (fixedRate) return fixedRate.rate;
        }
    }

    if (displayCurrencySource === 'live' && !_.isEmpty(lockedExchangeRates)) {
        if (lockedExchangeRates.live?.[sourceCurrency]) return lockedExchangeRates.live[displayCurrency] / lockedExchangeRates.live[sourceCurrency];
    }

    return liveRates?.[displayCurrency] / liveRates?.[sourceCurrency];
}

function getErrors({ opportunity }) {
    const er = { header: [], account: [], products: [], incoterm: [], builder: [] };

    if (!opportunity?.account?.id) {
        er.account.push({ id: 'id', message: 'Account is required' });
        er.header.push({ id: 'account-id', message: 'Account is required' });
    }

    if ((opportunity.account?.status === 'draft') && !_.trim(opportunity.account?.name)) er.account.push({ id: 'name', message: 'Account name is required' });

    opportunity.products.forEach((product, i) => {
        const e = checkProductErrors({ product });
        er.products = [...er.products, ...e.map(p => ({ id: `product:${i}:${p}`, message: p }))];
    });

    if (!opportunity.transportationMethod) {
        er.incoterm.push({ id: 'transportationMethod', message: 'Transportation method is required' });
        er.header.push({ id: 'transportationMethod', message: 'Transportation method is required' });
    }

    if (!opportunity.incoterm) {
        er.incoterm.push({ id: 'incoterm', message: 'Incoterm is required' });
        er.header.push({ id: 'incoterm', message: 'Incoterm is required' });
    }

    opportunity.offers.forEach((offer, oi) => {
        if (!offer.title) er.builder.push({ id: `${oi}:offertitle`, message: `Title is missing from the ${oi === 0 ? 'first' : oi === 1 ? 'second' : oi === 2 ? 'third' : oi === 3 ? 'fourth' : oi === 4 ? 'fifth' : 'sixth'} offer.` });
        offer.sections.forEach((section, si) =>
            section.offers.forEach((sectionOffer, soi) =>
                sectionOffer.fields.forEach((field, fi) => {
                    if (!field.type) er.builder.push({ id: `${oi}:${si}:${soi}:${fi}:type`, message: `Field (in offer ${oi}, section ${si}) type is required` });
                })
            )
        )
    });
    return er;
}

export { minimizeProducts, maximizeProducts, getProductInOffer, migrateOpportunity, setProductForOffer, 
    getOpportunitiesFromServer, getDisplayRate, getErrors };